import { useCallStore } from "../../stores"
import { api } from "../api/client"
import { disconnectAll, getActiveCall } from "../twilio"
import { hydrateCurrentCallFromServer, isTwilioCallSid } from "./current-call-sync"

const SID_LOOKUP_RETRY_DELAY_MS = 500
const SID_LOOKUP_MAX_ATTEMPTS = 6
const LIVE_CALL_STATUSES = ["open", "connecting", "pending", "ringing", "reconnecting"]

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, ms)
  })
}

export async function resolveCurrentCallSid(reason: "hold" | "hangup"): Promise<string> {
  for (let attempt = 0; attempt < SID_LOOKUP_MAX_ATTEMPTS; attempt++) {
    const currentStoreCall = useCallStore.getState().currentCall
    const currentStoreCallSid = currentStoreCall?.call_sid
    if (isTwilioCallSid(currentStoreCallSid)) {
      return currentStoreCallSid
    }

    const active = getActiveCall()
    const twilioCallSid = (active?.parameters as Record<string, string> | undefined)?.CallSid || ""
    if (isTwilioCallSid(twilioCallSid)) {
      return twilioCallSid
    }

    if (attempt === 0 || attempt === SID_LOOKUP_MAX_ATTEMPTS - 1) {
      const hydratedCall = await hydrateCurrentCallFromServer(`on-call-${reason}-sid-attempt-${attempt + 1}`, {
        clearIfMissing: attempt === SID_LOOKUP_MAX_ATTEMPTS - 1,
        hasLiveTwilioCall: Boolean(active && LIVE_CALL_STATUSES.includes(active.status())),
      })

      const hydratedCallSid = hydratedCall?.call_sid
      if (isTwilioCallSid(hydratedCallSid)) {
        return hydratedCallSid
      }
    }

    if (attempt < SID_LOOKUP_MAX_ATTEMPTS - 1) {
      await delay(SID_LOOKUP_RETRY_DELAY_MS)
    }
  }

  console.error(`[call-center] Unable to resolve Twilio CallSid for ${reason}`)
  return ""
}

export async function toggleHoldForCurrentCall(
  onHold: boolean,
  setOnHold: (value: boolean) => void
): Promise<"success" | "missing_sid" | "error"> {
  const callSid = await resolveCurrentCallSid("hold")
  if (!callSid) {
    return "missing_sid"
  }

  const currentHold = onHold
  setOnHold(!onHold)

  try {
    const response = await api.callCenter.hold({ callSid })
    if (!response.success) {
      setOnHold(currentHold)
      return "error"
    }

    return "success"
  } catch {
    setOnHold(currentHold)
    return "error"
  }
}

export async function hangupCurrentCall(): Promise<"api" | "fallback"> {
  const callSid = await resolveCurrentCallSid("hangup")
  if (!callSid) {
    console.error("[call-center] Falling back to local disconnect because no server-backed CallSid could be recovered")
    disconnectAll()
    return "fallback"
  }

  try {
    await api.callCenter.hangup({ callSid })
    return "api"
  } catch (error) {
    console.error("[call-center] Server hangup failed — falling back to local disconnect", error)
    disconnectAll()
    return "fallback"
  }
}
